/**
 * Diseño y producción (antes "Mi Producción") — Kanban operativo de diseño/producción.
 *
 * - 6 columnas (Pendiente diseño → En diseño → AAFF enviado → AAFF aprobado → En producción → Producido).
 * - Drag & drop desktop (@dnd-kit/core) + botón "→ Siguiente paso" en móvil.
 * - Header con KPIs animados.
 * - Botón flotante "🏷️ Imprimir N etiquetas" cuando hay etiquetas pendientes.
 *
 * Visible para: roles "diseño", "produccion", "admin".
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  DndContext, PointerSensor, useSensor, useSensors, DragOverlay, closestCorners,
} from "@dnd-kit/core";
import { useDroppable, useDraggable } from "@dnd-kit/core";
import {
  Palette, Loader2, Printer, AlertTriangle, Calendar, Tag, Package,
  TrendingUp, Clock, Sparkles, ChevronRight, Eye, ArrowUp, ArrowDown, Paperclip,
} from "lucide-react";
import ComponenteDetailModal from "@/components/ComponenteDetailModal";
import BuscadorLineas from "@/components/BuscadorLineas";
import { GraficaChip } from "@/components/PermisoCorreo";

// Iter 105 — opciones de ordenación (global y por columna)
const ORDEN_OPTS = [
  { v: "manual", label: "⠿ Manual (taller)" },
  { v: "cliente", label: "Cliente A→Z" },
  { v: "marca", label: "Marca A→Z" },
  { v: "fecha_solicitud", label: "Entrada petición ↑" },
  { v: "entrada_columna", label: "Más tiempo en columna" },
  { v: "fecha_montaje", label: "Fecha montaje ↑" },
  { v: "provincia", label: "Provincia A→Z" },
  { v: "espacio", label: "Espacio A→Z" },
  { v: "material", label: "Material A→Z" },
];

function cmpPara(orden) {
  const s = (x) => (x || "\uffff").toString();
  switch (orden) {
    case "cliente":
      return (a, b) => s(a.cliente?.nombre).localeCompare(s(b.cliente?.nombre));
    case "marca":
      return (a, b) => s(a.peticion?.marca).localeCompare(s(b.peticion?.marca));
    case "fecha_solicitud":
      return (a, b) => (a.peticion?.fecha_solicitud || "9999").localeCompare(b.peticion?.fecha_solicitud || "9999");
    case "entrada_columna":
      return (a, b) => (a.updated_at || "9999").localeCompare(b.updated_at || "9999");
    case "fecha_montaje":
      return (a, b) => (a.fecha_montaje || "9999").localeCompare(b.fecha_montaje || "9999");
    case "provincia":
      return (a, b) => s(a.espacio?.provincia).localeCompare(s(b.espacio?.provincia), "es");
    case "espacio":
      return (a, b) => s(a.espacio?.nombre).localeCompare(s(b.espacio?.nombre), "es");
    case "material":
      return (a, b) => s(a.material?.nombre).localeCompare(s(b.material?.nombre), "es");
    default: // manual
      return (a, b) => (a.orden_produccion ?? 1e9) - (b.orden_produccion ?? 1e9);
  }
}

// Iter 113 · Colores por provincia (misma paleta del calendario de montajes)
const PROVINCIA_COLOR = {
  "cádiz":    "bg-amber-400/30 text-amber-100 border-amber-400/60",
  "cadiz":    "bg-amber-400/30 text-amber-100 border-amber-400/60",
  "sevilla":  "bg-lime-500/25 text-lime-100 border-lime-500/50",
  "huelva":   "bg-sky-500/25 text-sky-100 border-sky-500/50",
  "málaga":   "bg-purple-500/25 text-purple-100 border-purple-500/50",
  "malaga":   "bg-purple-500/25 text-purple-100 border-purple-500/50",
  "portugal": "bg-orange-500/25 text-orange-100 border-orange-500/50",
  "córdoba":  "bg-rose-500/25 text-rose-100 border-rose-500/50",
  "cordoba":  "bg-rose-500/25 text-rose-100 border-rose-500/50",
  "granada":  "bg-fuchsia-500/25 text-fuchsia-100 border-fuchsia-500/50",
  "almería":  "bg-teal-500/25 text-teal-100 border-teal-500/50",
  "almeria":  "bg-teal-500/25 text-teal-100 border-teal-500/50",
  "jaén":     "bg-yellow-600/30 text-yellow-100 border-yellow-600/50",
  "jaen":     "bg-yellow-600/30 text-yellow-100 border-yellow-600/50",
};
const provinciaColor = (p) => {
  if (!p) return "bg-zinc-700/40 text-zinc-300 border-zinc-600/60";
  return PROVINCIA_COLOR[p.toLowerCase().trim()] || "bg-zinc-700/40 text-zinc-300 border-zinc-600/60";
};

// Helper para agrupar histórico por día (Hoy / Ayer / dd/mm/aaaa)
// Iter 115 · Items sin `updated_at` van al bucket "Sin fecha" para que el
// contador de la columna coincida con las cards visibles.
function agruparPorDia(items) {
  const hoy = new Date();
  const ayer = new Date(hoy);
  ayer.setDate(hoy.getDate() - 1);
  const k = (d) => d.toISOString().slice(0, 10);
  const kHoy = k(hoy);
  const kAyer = k(ayer);
  const map = new Map();
  const sinFecha = { iso: "0000-00-00", label: "Sin fecha", items: [] };
  for (const it of items) {
    const iso = (it.updated_at || "").slice(0, 10);
    if (!iso) { sinFecha.items.push(it); continue; }
    const label = iso === kHoy ? "Hoy" : iso === kAyer ? "Ayer"
      : new Date(iso + "T00:00:00").toLocaleDateString("es-ES", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
    const bucket = map.get(iso) || { iso, label, items: [] };
    bucket.items.push(it);
    map.set(iso, bucket);
  }
  // orden: DESC por fecha; "Sin fecha" al final si aplica
  const arr = Array.from(map.values()).sort((a, b) => (a.iso < b.iso ? 1 : -1));
  if (sinFecha.items.length) arr.push(sinFecha);
  return arr;
}

// Fase D · agrupar tarjetas por material (cola por máquina). Mantiene el orden ya aplicado dentro de cada grupo.
function agruparPorMaterial(items) {
  const map = new Map();
  for (const it of items) {
    const label = it.material?.nombre || "Sin material";
    const key = label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    const g = map.get(key) || { key, label, items: [], m2: 0 };
    g.items.push(it);
    if (it.m2) g.m2 += it.m2 * (it.cantidad || 1);
    map.set(key, g);
  }
  return Array.from(map.values())
    .map((g) => ({ ...g, m2: Math.round(g.m2 * 100) / 100 }))
    .sort((a, b) => (a.label === "Sin material") - (b.label === "Sin material") || a.label.localeCompare(b.label, "es"));
}

const COLOR_CLS = {
  zinc:    { bg: "bg-zinc-700/15",    border: "border-zinc-700/40",    text: "text-zinc-300",    chip: "bg-zinc-700/40 text-zinc-200" },
  violet:  { bg: "bg-violet-500/10",  border: "border-violet-500/30",  text: "text-violet-200",  chip: "bg-violet-500/30 text-violet-100" },
  sky:     { bg: "bg-sky-500/10",     border: "border-sky-500/30",     text: "text-sky-200",     chip: "bg-sky-500/30 text-sky-100" },
  emerald: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-200", chip: "bg-emerald-500/30 text-emerald-100" },
  orange:  { bg: "bg-orange-500/10",  border: "border-orange-500/30",  text: "text-orange-200",  chip: "bg-orange-500/30 text-orange-100" },
  amber:   { bg: "bg-amber-500/15",   border: "border-amber-500/40",   text: "text-amber-200",   chip: "bg-amber-500/40 text-amber-100" },
};

export default function MiProduccion() {
  const [kanban, setKanban] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCard, setActiveCard] = useState(null);
  const [printing, setPrinting] = useState(false);
  // Iter 67 — modal de detalle de componente
  const [detailId, setDetailId] = useState(null);
  // Iter 101 — filtro global + límite 30 días en "Producido"
  const [filtro, setFiltro] = useState("");
  const [filtroCliente, setFiltroCliente] = useState("");
  const [filtroMarca, setFiltroMarca] = useState("");
  // Iter 113 — nuevos filtros + presets + modo compacto + fullscreen
  const [filtroEspacio, setFiltroEspacio] = useState("");
  const [filtroProvincia, setFiltroProvincia] = useState("");
  const [filtroMaterial, setFiltroMaterial] = useState("");
  const [verTodosProducidos, setVerTodosProducidos] = useState(false);
  const [modoCompacto, setModoCompacto] = useState(() => localStorage.getItem("dnf-prod-compacto") === "1");
  const [fullscreen, setFullscreen] = useState(false);
  // Fase D (Sep-2026) · filtros rápidos (chips) + agrupar por material dentro de cada columna
  const [quick, setQuick] = useState(() => { try { return JSON.parse(localStorage.getItem("dnf-prod-quick")) || []; } catch { return []; } });
  const [agruparMaterial, setAgruparMaterial] = useState(() => localStorage.getItem("dnf-prod-agrupar-material") === "1");
  useEffect(() => { localStorage.setItem("dnf-prod-quick", JSON.stringify(quick)); }, [quick]);
  useEffect(() => { localStorage.setItem("dnf-prod-agrupar-material", agruparMaterial ? "1" : "0"); }, [agruparMaterial]);
  const toggleQuick = (k) => setQuick((q) => (q.includes(k) ? q.filter((x) => x !== k) : [...q, k]));
  // Colapsables histórico: por defecto colapsados los días > 14 días
  const [colapsados, setColapsados] = useState({});
  useEffect(() => { localStorage.setItem("dnf-prod-compacto", modoCompacto ? "1" : "0"); }, [modoCompacto]);

  // Iter 113 — presets guardados
  const PRESETS_KEY = "dnf-prod-presets-v1";
  const [presets, setPresets] = useState(() => {
    try { return JSON.parse(localStorage.getItem(PRESETS_KEY)) || []; } catch { return []; }
  });
  // Iter 105 — ordenación global + por columna (persistida en localStorage)
  const OS_KEY = "dnf-produccion-orden-v1";
  const _os = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(OS_KEY)) || {}; } catch { return {}; }
  }, []);
  const [ordenGlobal, setOrdenGlobal] = useState(_os.global || "manual");
  const [ordenCols, setOrdenCols] = useState(_os.cols || {});
  useEffect(() => {
    try { localStorage.setItem(OS_KEY, JSON.stringify({ global: ordenGlobal, cols: ordenCols })); } catch {}
  }, [ordenGlobal, ordenCols]);
  const ordenDe = (colKey) => ordenCols[colKey] || ordenGlobal;

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const cargar = useCallback(async () => {
    try {
      const [k, p] = await Promise.all([
        api.get("/produccion/kanban"),
        api.get("/produccion/kpis"),
      ]);
      setKanban(k.data);
      setKpis(p.data);
    } catch (e) {
      toast.error("Error cargando: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiarEstado = async (cid, nuevoEstado) => {
    try {
      await api.patch(`/componentes/${cid}`, { estado: nuevoEstado });
      toast.success("Estado actualizado");
      cargar();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
      cargar();
    }
  };

  const onDragStart = (ev) => {
    const cid = ev.active.id;
    const card = Object.values(kanban?.grupos || {}).flat().find((c) => c.id === cid);
    setActiveCard(card);
  };

  const onDragEnd = (ev) => {
    setActiveCard(null);
    const cid = ev.active.id;
    const overCol = ev.over?.id;
    if (!overCol) return;
    const card = Object.values(kanban?.grupos || {}).flat().find((c) => c.id === cid);
    if (!card || card.estado === overCol) return;
    cambiarEstado(cid, overCol);
  };

  const imprimirEtiquetasPendientes = async () => {
    setPrinting(true);
    try {
      const { data } = await api.get("/produccion/etiquetas-pendientes");
      if (!data.componentes.length) { toast.info("No hay etiquetas pendientes"); return; }
      // Generamos los PDFs uno a uno y los abrimos en una sola pestaña con todo concatenado
      // Para no abrir 50 pestañas, usamos el endpoint existente de etiqueta por componente:
      // GET /api/componentes/{cid}/etiqueta-brother.pdf
      // Por simplicidad: abrimos cada etiqueta en una pestaña nueva.
      // Mejor UX: usar window.open con timing escalonado.
      const ids = data.componentes.map((c) => c.id);
      for (let i = 0; i < ids.length; i++) {
        // eslint-disable-next-line no-await-in-loop
        const r = await api.get(`/componentes/${ids[i]}/etiqueta.pdf`, { responseType: "blob" });
        const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 30000);
        // pequeña espera para no saturar el popup blocker
        if (i < ids.length - 1) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 250));
        }
      }
      // Marcar como impresas
      await api.post("/produccion/marcar-impresas", { ids });
      toast.success(`${ids.length} etiqueta(s) enviada(s) a imprimir`);
      cargar();
    } catch (e) {
      toast.error("Error imprimiendo: " + (e?.response?.data?.detail || e.message));
    } finally {
      setPrinting(false);
    }
  };

  // Buscador transversal (Jun-2026): filtra el Kanban por el código de petición y abre el detalle
  const enfocarDesdeBuscador = (hit, comp) => {
    setFiltro(hit.peticion_codigo || "");
    setFiltroCliente(""); setFiltroMarca(""); setFiltroEspacio(""); setFiltroProvincia(""); setFiltroMaterial(""); setQuick([]);
    setVerTodosProducidos(true);
    const cid = comp?.id || hit.componentes?.[0]?.id;
    if (cid) setDetailId(cid);
    else toast.info(`${hit.peticion_codigo}: la línea no tiene componentes en producción`);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
    </div>
  );

  const allCards = Object.values(kanban?.grupos || {}).flat();
  const cardsActivas = Object.entries(kanban?.grupos || {}).filter(([k]) => k !== "historico").flatMap(([, v]) => v);
  const clientesOpts = [...new Set(allCards.map((c) => c.cliente?.nombre).filter(Boolean))].sort();
  const marcasOpts = [...new Set(allCards.map((c) => c.peticion?.marca).filter(Boolean))].sort();
  const espaciosOpts = [...new Set(allCards.map((c) => c.espacio?.nombre).filter(Boolean))].sort();
  const provinciasOpts = [...new Set(allCards.map((c) => c.espacio?.provincia).filter(Boolean))].sort();
  const materialesOpts = [...new Set(allCards.map((c) => c.material?.nombre).filter(Boolean))].sort();
  const hayFiltro = filtro.trim() || filtroCliente || filtroMarca || filtroEspacio || filtroProvincia || filtroMaterial || quick.length > 0;

  const txt = filtro.trim().toLowerCase();
  const corte30d = new Date(Date.now() - 30 * 86400000).toISOString();
  const en7d = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  const hoyIso = new Date().toISOString().slice(0, 10);
  const cumpleQuick = (c, q) => {
    const d = c.peticion?.dias_a_objetivo;
    switch (q) {
      case "vencidos": return d !== null && d !== undefined && d < 0;
      case "urgentes": return d !== null && d !== undefined && d >= 0 && d <= 3;
      case "sin_aaff": return (c.aaff_total || 0) === 0;
      case "montaje_7d": return !!c.fecha_montaje && c.fecha_montaje.slice(0, 10) >= hoyIso && c.fecha_montaje.slice(0, 10) <= en7d;
      case "marcos": return c.tipo_plantilla === "marco_vinilo";
      case "etiquetas_pendientes": return c.estado === "producido" && !c.etiqueta_impresa_at;
      default: return true;
    }
  };
  const pasaQuick = (c) => quick.every((q) => cumpleQuick(c, q));
  const gruposFiltrados = {};
  let ocultosProducido = 0;
  for (const [key, items] of Object.entries(kanban?.grupos || {})) {
    let lst = items.filter((c) => {
      if (filtroCliente && c.cliente?.nombre !== filtroCliente) return false;
      if (filtroMarca && c.peticion?.marca !== filtroMarca) return false;
      if (filtroEspacio && c.espacio?.nombre !== filtroEspacio) return false;
      if (filtroProvincia && c.espacio?.provincia !== filtroProvincia) return false;
      if (filtroMaterial && c.material?.nombre !== filtroMaterial) return false;
      if (quick.length && !pasaQuick(c)) return false;
      if (txt) {
        const blob = `${c.nombre || ""} ${c.peticion?.codigo || ""} ${c.peticion?.nombre || ""} ${c.cliente?.nombre || ""} ${c.peticion?.marca || ""} ${c.espacio?.nombre || ""} ${c.espacio?.provincia || ""} ${c.material?.nombre || ""}`.toLowerCase();
        if (!blob.includes(txt)) return false;
      }
      return true;
    });
    if (key === "producido" && !verTodosProducidos) {
      const recientes = lst.filter((c) => (c.updated_at || "") >= corte30d);
      ocultosProducido = lst.length - recientes.length;
      lst = recientes;
    }
    gruposFiltrados[key] = [...lst].sort(cmpPara(ordenDe(key)));
  }

  // Iter 113 · Sumar m² por columna a partir de los filtrados
  const m2FiltradosPorCol = {};
  for (const [k, lst] of Object.entries(gruposFiltrados)) {
    let m2 = 0;
    for (const c of lst) if (c.m2) m2 += c.m2 * (c.cantidad || 1);
    m2FiltradosPorCol[k] = Math.round(m2 * 100) / 100;
  }

  // Iter 113 · presets — guardar / cargar / borrar
  const filtrosActuales = () => ({
    filtro, filtroCliente, filtroMarca, filtroEspacio, filtroProvincia, filtroMaterial,
    ordenGlobal, ordenCols,
  });
  const savePreset = () => {
    const nombre = window.prompt("Nombre del preset (ej: Cádiz urgente)");
    if (!nombre) return;
    const next = [...presets.filter((p) => p.nombre !== nombre), { nombre, data: filtrosActuales() }];
    setPresets(next);
    localStorage.setItem(PRESETS_KEY, JSON.stringify(next));
    toast.success(`Preset "${nombre}" guardado`);
  };
  const applyPreset = (p) => {
    const d = p.data || {};
    setFiltro(d.filtro || ""); setFiltroCliente(d.filtroCliente || ""); setFiltroMarca(d.filtroMarca || "");
    setFiltroEspacio(d.filtroEspacio || ""); setFiltroProvincia(d.filtroProvincia || "");
    setFiltroMaterial(d.filtroMaterial || "");
    setOrdenGlobal(d.ordenGlobal || "manual"); setOrdenCols(d.ordenCols || {});
    toast.success(`Preset "${p.nombre}" aplicado`);
  };
  const deletePreset = (nombre) => {
    if (!window.confirm(`¿Borrar preset "${nombre}"?`)) return;
    const next = presets.filter((p) => p.nombre !== nombre);
    setPresets(next); localStorage.setItem(PRESETS_KEY, JSON.stringify(next));
  };

  const copiarCodigo = async (codigo) => {
    try { await navigator.clipboard.writeText(codigo); toast.success(`Copiado: ${codigo}`); }
    catch { toast.error("No se pudo copiar"); }
  };

  const archivarComponente = async (cid) => {
    try {
      await api.post("/produccion/archivar-historico", { ids: [cid] });
      toast.success("Enviado a histórico");
      cargar();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    }
  };

  // Fase D · etiqueta Brother individual desde la tarjeta (Producido)
  const imprimirEtiqueta = async (cid) => {
    try {
      const r = await api.get(`/componentes/${cid}/etiqueta.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      await api.post("/produccion/marcar-impresas", { ids: [cid] });
      toast.success("Etiqueta enviada a imprimir");
      cargar();
    } catch (e) {
      toast.error("Error imprimiendo: " + (e?.response?.data?.detail || e.message));
    }
  };

  const QUICK_CHIPS = [
    { k: "vencidos", label: "⚠ Vencidos", tip: "Componentes cuya petición tiene la fecha objetivo pasada", on: "bg-red-500/25 border-red-500/60 text-red-100" },
    { k: "urgentes", label: "⏰ ≤3 días", tip: "Componentes cuya petición vence en 3 días o menos", on: "bg-amber-500/25 border-amber-500/60 text-amber-100" },
    { k: "montaje_7d", label: "📅 Montaje 7 días", tip: "Con fecha de montaje en los próximos 7 días", on: "bg-sky-500/25 border-sky-500/60 text-sky-100" },
    { k: "sin_aaff", label: "📎 Sin AAFF", tip: "Sin ningún archivo AAFF accesible", on: "bg-violet-500/25 border-violet-500/60 text-violet-100" },
    { k: "marcos", label: "🖼 Marcos", tip: "Marcos de vinilo (con sub-piezas)", on: "bg-emerald-500/25 border-emerald-500/60 text-emerald-100" },
    { k: "etiquetas_pendientes", label: "🏷️ Sin etiqueta", tip: "Producidos sin etiqueta impresa", on: "bg-amber-500/25 border-amber-500/60 text-amber-100" },
  ];
  const quickCounts = Object.fromEntries(QUICK_CHIPS.map((ch) => [ch.k, cardsActivas.filter((c) => cumpleQuick(c, ch.k)).length]));

  // Iter 105 — mover tarjeta arriba/abajo en orden manual (compartido)
  const moverCard = async (colKey, displayed, cid, dir) => {
    const idx = displayed.findIndex((c) => c.id === cid);
    const j = idx + (dir === "up" ? -1 : 1);
    if (idx < 0 || j < 0 || j >= displayed.length) return;
    const vecinoId = displayed[j].id;
    const full = [...(kanban?.grupos?.[colKey] || [])].sort(cmpPara("manual")).map((c) => c.id);
    const from = full.indexOf(cid);
    if (from < 0) return;
    full.splice(from, 1);
    let to = full.indexOf(vecinoId);
    if (dir === "down") to += 1;
    full.splice(to, 0, cid);
    const ordenMap = Object.fromEntries(full.map((id, i) => [id, i]));
    setKanban((k) => ({
      ...k,
      grupos: {
        ...k.grupos,
        [colKey]: k.grupos[colKey].map((c) =>
          ordenMap[c.id] !== undefined ? { ...c, orden_produccion: ordenMap[c.id] } : c),
      },
    }));
    try {
      await api.post("/produccion/orden", { ids: full });
    } catch (e) {
      toast.error("Error guardando orden: " + (e?.response?.data?.detail || e.message));
      cargar();
    }
  };

  return (
    <div className={fullscreen ? "p-2" : "p-4 w-full"}>
      {!fullscreen && (
        <PageHeader
          icon={Palette}
          title="Diseño y producción"
          subtitle={`Kanban de diseño y producción · ${kpis?.mes_actual || ""}`}
          action={
            <a href="/produccion/calculadora-pegatinas"
              data-testid="link-calc-pegatinas"
              className="inline-flex items-center gap-1.5 text-xs bg-amber-500/15 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30 rounded-md px-3 py-1.5 transition-colors">
              🏷️ Calculadora pegatinas
            </a>
          }
        />
      )}

      {/* KPIs (ocultos en fullscreen) */}
      {!fullscreen && <KPIGrid kpis={kpis} quick={quick} onQuick={(k) => setQuick((q) => (q.includes(k) ? q.filter((x) => x !== k) : [k]))} />}

      {/* Jun-2026 (Elia) — buscador transversal: enfoca el Kanban en la petición y abre el detalle del componente */}
      {!fullscreen && (
        <div className="mt-4">
          <BuscadorLineas
            testId="buscador-produccion"
            onSelect={enfocarDesdeBuscador}
            placeholder="Buscar en producción: código DNF, cliente, PDV, ciudad, marca, ref. cliente, componente…"
            hint="pulsa un resultado para enfocar el Kanban y abrir su detalle" />
        </div>
      )}

      {/* Iter 101/113 — filtros extendidos */}
      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="filtros-produccion">
        <input
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          placeholder="🔍 Filtrar por texto, código, cliente, marca, espacio, provincia, material…"
          data-testid="filtro-texto"
          className="flex-1 min-w-[260px] bg-zinc-900/60 border border-zinc-800 rounded-md px-3 py-1.5 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50"
        />
        <select value={filtroCliente} onChange={(e) => setFiltroCliente(e.target.value)}
          data-testid="filtro-cliente"
          className="bg-zinc-900/60 border border-zinc-800 rounded-md px-2 py-1.5 text-sm text-zinc-300 max-w-[180px]">
          <option value="">Todos los clientes</option>
          {clientesOpts.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <select value={filtroMarca} onChange={(e) => setFiltroMarca(e.target.value)}
          data-testid="filtro-marca"
          className="bg-zinc-900/60 border border-zinc-800 rounded-md px-2 py-1.5 text-sm text-zinc-300 max-w-[160px]">
          <option value="">Todas las marcas</option>
          {marcasOpts.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={filtroEspacio} onChange={(e) => setFiltroEspacio(e.target.value)}
          data-testid="filtro-espacio"
          className="bg-zinc-900/60 border border-zinc-800 rounded-md px-2 py-1.5 text-sm text-zinc-300 max-w-[180px]">
          <option value="">Todos los espacios</option>
          {espaciosOpts.map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
        <select value={filtroProvincia} onChange={(e) => setFiltroProvincia(e.target.value)}
          data-testid="filtro-provincia"
          className="bg-zinc-900/60 border border-zinc-800 rounded-md px-2 py-1.5 text-sm text-zinc-300 max-w-[140px]">
          <option value="">Todas las provincias</option>
          {provinciasOpts.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filtroMaterial} onChange={(e) => setFiltroMaterial(e.target.value)}
          data-testid="filtro-material"
          className="bg-zinc-900/60 border border-zinc-800 rounded-md px-2 py-1.5 text-sm text-zinc-300 max-w-[160px]">
          <option value="">Todos los materiales</option>
          {materialesOpts.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={ordenGlobal}
          onChange={(e) => { setOrdenGlobal(e.target.value); setOrdenCols({}); }}
          data-testid="orden-global"
          title="Ordenar todas las columnas"
          className="bg-zinc-900/60 border border-zinc-800 rounded-md px-2 py-1.5 text-sm text-zinc-300 max-w-[200px]">
          {ORDEN_OPTS.map((o) => <option key={o.v} value={o.v}>↕ {o.label}</option>)}
        </select>
        {hayFiltro && (
          <button
            onClick={() => { setFiltro(""); setFiltroCliente(""); setFiltroMarca(""); setFiltroEspacio(""); setFiltroProvincia(""); setFiltroMaterial(""); setQuick([]); }}
            data-testid="limpiar-filtros"
            className="text-xs px-2.5 py-1.5 rounded-md bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20 transition-colors">
            ✕ Limpiar
          </button>
        )}
      </div>

      {/* Fase D · filtros rápidos de un clic (combinables) + agrupar por material */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs" data-testid="filtros-rapidos">
        {QUICK_CHIPS.map((ch) => {
          const on = quick.includes(ch.k);
          const n = quickCounts[ch.k] || 0;
          return (
            <button key={ch.k} type="button" onClick={() => toggleQuick(ch.k)}
              data-testid={`quick-${ch.k}`} aria-pressed={on} title={ch.tip}
              className={`px-2.5 py-1 rounded-full border transition-colors ${on
                ? `${ch.on} font-semibold`
                : n > 0 ? "bg-zinc-900/60 border-zinc-700 text-zinc-300 hover:border-zinc-500" : "bg-zinc-900/30 border-zinc-800 text-zinc-600"}`}>
              {ch.label} <span className="font-mono opacity-80">{n}</span>
            </button>
          );
        })}
        <div className="flex-1" />
        <button type="button" onClick={() => setAgruparMaterial((v) => !v)}
          data-testid="toggle-agrupar-material" aria-pressed={agruparMaterial}
          className={`px-2.5 py-1 rounded-full border ${agruparMaterial ? "bg-amber-500/20 border-amber-500/40 text-amber-200 font-semibold" : "bg-zinc-900/60 border-zinc-800 text-zinc-300"}`}>
          🏭 Agrupar por material {agruparMaterial ? "✓" : ""}
        </button>
      </div>

      {/* Fila de utilidades: presets + modo compacto + fullscreen */}
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-testid="utilidades-produccion">
        <button onClick={savePreset}
          data-testid="preset-save"
          className="px-2 py-1 rounded bg-amber-500/10 border border-amber-500/30 text-amber-300 hover:bg-amber-500/20">
          💾 Guardar preset
        </button>
        {presets.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-zinc-500">Presets:</span>
            {presets.map((p) => (
              <span key={p.nombre} className="inline-flex items-center gap-1 bg-zinc-900/60 border border-zinc-800 rounded pl-2 pr-1 py-0.5">
                <button onClick={() => applyPreset(p)}
                  data-testid={`preset-apply-${p.nombre}`}
                  className="text-zinc-200 hover:text-amber-300">{p.nombre}</button>
                <button onClick={() => deletePreset(p.nombre)}
                  data-testid={`preset-del-${p.nombre}`}
                  className="text-zinc-600 hover:text-red-400 text-[10px] px-1">×</button>
              </span>
            ))}
          </div>
        )}
        <div className="flex-1" />
        <button onClick={() => setModoCompacto((v) => !v)}
          data-testid="toggle-compacto"
          className={`px-2 py-1 rounded border ${modoCompacto ? "bg-amber-500/20 border-amber-500/40 text-amber-200" : "bg-zinc-900/60 border-zinc-800 text-zinc-300"}`}>
          {modoCompacto ? "◱ Modo compacto ✓" : "◱ Modo compacto"}
        </button>
        <button onClick={() => setFullscreen((v) => !v)}
          data-testid="toggle-fullscreen"
          className={`px-2 py-1 rounded border ${fullscreen ? "bg-amber-500/20 border-amber-500/40 text-amber-200" : "bg-zinc-900/60 border-zinc-800 text-zinc-300"}`}>
          {fullscreen ? "⤢ Salir de pantalla completa" : "⤢ Pantalla completa"}
        </button>
      </div>

      {/* Kanban */}
      <DndContext sensors={sensors} collisionDetection={closestCorners}
        onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div className="mt-4 grid gap-2 items-start"
             style={{ gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}
             data-testid="kanban-grid">
          {kanban?.columnas.map((col) => (
            <Columna key={col.key} col={col} items={gruposFiltrados[col.key] || []}
                     onCambiarEstado={cambiarEstado}
                     onOpenDetail={setDetailId}
                     onCopiar={copiarCodigo}
                     onArchivar={archivarComponente}
                     onEtiqueta={col.key === "producido" ? imprimirEtiqueta : null}
                     agruparMaterial={agruparMaterial}
                     m2Total={m2FiltradosPorCol[col.key] || 0}
                     modoCompacto={modoCompacto}
                     colapsados={colapsados}
                     setColapsados={setColapsados}
                     orden={ordenDe(col.key)}
                     esOverride={!!ordenCols[col.key]}
                     onOrdenChange={(v) => setOrdenCols((prev) => {
                       const next = { ...prev };
                       if (!v || v === ordenGlobal) delete next[col.key]; else next[col.key] = v;
                       return next;
                     })}
                     onMover={(cid, dir) => moverCard(col.key, gruposFiltrados[col.key] || [], cid, dir)}
                     producidoInfo={col.key === "producido" ? {
                       ocultos: ocultosProducido,
                       verTodos: verTodosProducidos,
                       onToggle: () => setVerTodosProducidos((v) => !v),
                     } : null} />
          ))}
        </div>
        <DragOverlay>
          {activeCard ? <CardComp c={activeCard} arrastrando /> : null}
        </DragOverlay>
      </DndContext>

      {/* Botón flotante imprimir */}
      {(kpis?.etiquetas_pendientes || 0) > 0 && (
        <button
          onClick={imprimirEtiquetasPendientes}
          disabled={printing}
          data-testid="imprimir-etiquetas-pendientes"
          className="fixed bottom-6 right-6 z-40 bg-amber-500 hover:bg-amber-400 disabled:opacity-60
                     text-zinc-950 font-bold px-5 py-3 rounded-full shadow-lg shadow-amber-500/30
                     flex items-center gap-2 transition-all hover:scale-105">
          {printing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Printer className="w-5 h-5" />}
          Imprimir {kpis.etiquetas_pendientes} etiqueta{kpis.etiquetas_pendientes !== 1 ? "s" : ""}
        </button>
      )}

      {/* Modal de detalle del componente */}
      <ComponenteDetailModal
        open={!!detailId}
        componenteId={detailId}
        onClose={() => setDetailId(null)}
        onEstadoChange={() => cargar()}
      />
    </div>
  );
}

const KPIGrid = ({ kpis, quick = [], onQuick }) => kpis ? (
  <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="kpis-grid">
    <KpiCard label="Activos" value={kpis.activos} icon={Sparkles} color="text-amber-300" tip="Componentes en producción"
             testid="kpi-activos" />
    <KpiCard label="AAFF cliente" value={kpis.aaff_pendiente_cliente} icon={Clock} color="text-sky-300"
             tip="Esperando OK del cliente" testid="kpi-aaff" warn={kpis.aaff_pendiente_cliente > 5} />
    <KpiCard label="Vencidos" value={kpis.vencidos} icon={AlertTriangle}
             color={kpis.vencidos > 0 ? "text-red-300" : "text-zinc-400"}
             tip="Petición vencida sin producir · clic para filtrar" testid="kpi-vencidos" warn={kpis.vencidos > 0}
             active={quick.includes("vencidos")} onClick={() => onQuick?.("vencidos")} />
    <KpiCard label="Urgentes 3d" value={kpis.urgentes_3d} icon={AlertTriangle}
             color={kpis.urgentes_3d > 0 ? "text-orange-300" : "text-zinc-400"}
             tip="Vencen en ≤3 días · clic para filtrar" testid="kpi-urgentes" warn={kpis.urgentes_3d > 0}
             active={quick.includes("urgentes")} onClick={() => onQuick?.("urgentes")} />
    <KpiCard label="Etiquetas listas" value={kpis.etiquetas_pendientes} icon={Printer}
             color={kpis.etiquetas_pendientes > 0 ? "text-amber-300" : "text-emerald-300"}
             tip="Producidas, falta imprimir · clic para filtrar" testid="kpi-etiquetas" warn={kpis.etiquetas_pendientes > 0}
             active={quick.includes("etiquetas_pendientes")} onClick={() => onQuick?.("etiquetas_pendientes")} />
    <KpiCard label="Mes" value={kpis.producidos_mes} icon={TrendingUp} color="text-emerald-300"
             tip={`Producidas ${kpis.mes_actual}`} testid="kpi-mes" />
  </div>
) : null;

const KpiCard = ({ label, value, icon: Icon, color, tip, testid, warn, active, onClick }) => (
  <div className={`bg-zinc-900/40 border ${active ? "border-amber-400 ring-1 ring-amber-400/40" : warn ? "border-amber-500/40" : "border-zinc-800"} rounded-lg p-3 hover:border-zinc-700 transition-all ${onClick ? "cursor-pointer" : ""}`}
       title={tip} data-testid={testid} onClick={onClick} role={onClick ? "button" : undefined} aria-pressed={onClick ? !!active : undefined}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-[10px] text-zinc-500 uppercase tracking-wide">{label}</span>
      <Icon className={`w-3.5 h-3.5 ${color}`} />
    </div>
    <div className={`text-2xl font-bold ${color} font-mono`}>{value ?? "—"}</div>
  </div>
);

// ============ Columna del kanban ============
function Columna({ col, items, onCambiarEstado, onOpenDetail, onCopiar, onArchivar, onEtiqueta, agruparMaterial,
                   producidoInfo, orden, esOverride, onOrdenChange, onMover,
                   m2Total, modoCompacto, colapsados, setColapsados }) {
  const { setNodeRef, isOver } = useDroppable({ id: col.key, disabled: col.virtual });
  const cls = COLOR_CLS[col.color] || COLOR_CLS.zinc;
  const manual = orden === "manual";
  const esHistorico = col.key === "historico";
  const gruposDia = esHistorico ? agruparPorDia(items) : null;
  // Fase D · cola por material/máquina dentro de la columna (sin tocar el orden interno)
  const gruposMaterial = (!esHistorico && agruparMaterial) ? agruparPorMaterial(items) : null;

  const renderCard = (c, idx, lista) => (
    <DraggableCard key={c.id} c={c} siguiente={col.siguiente}
                   onSiguiente={onCambiarEstado}
                   onOpenDetail={onOpenDetail}
                   onCopiar={onCopiar}
                   onArchivar={col.key === "producido" ? onArchivar : null}
                   onEtiqueta={onEtiqueta}
                   compacto={modoCompacto}
                   mover={manual && !gruposMaterial ? {
                     up: idx > 0 ? () => onMover(c.id, "up") : null,
                     down: idx < lista.length - 1 ? () => onMover(c.id, "down") : null,
                   } : null} />
  );

  return (
    <div ref={setNodeRef}
         className={`rounded-lg border ${cls.border} ${cls.bg} ${isOver ? "ring-2 ring-amber-400/60 scale-[1.01]" : ""} transition-all`}
         data-testid={`col-${col.key}`}>
      <div className={`px-2 py-2 sticky top-0 bg-zinc-950/95 backdrop-blur border-b ${cls.border} rounded-t-lg z-10`}>
        <div className="flex items-center justify-between">
          <div className={`flex items-center gap-1.5 text-xs font-semibold ${cls.text}`}>
            <span className="text-base">{col.icon}</span>
            <span className="truncate">{col.label}</span>
          </div>
          <span className={`text-[10px] font-bold ${cls.chip} px-2 py-0.5 rounded-full font-mono`}>{items.length}</span>
        </div>
        {/* Iter 113 · m² totales por columna */}
        {m2Total > 0 && (
          <div className="mt-0.5 text-[9px] text-zinc-500 font-mono" title="m² totales sumados">
            📐 {m2Total} m²
          </div>
        )}
        {!esHistorico && (
          <select
            value={orden}
            onChange={(e) => onOrdenChange(e.target.value)}
            data-testid={`orden-col-${col.key}`}
            title="Ordenar esta columna"
            className={`mt-1 w-full bg-transparent border rounded px-1 py-0.5 text-[10px] focus:outline-none cursor-pointer
                        ${esOverride ? "border-amber-500/50 text-amber-300" : "border-zinc-800/60 text-zinc-500 hover:text-zinc-300"}`}>
            {ORDEN_OPTS.map((o) => (
              <option key={o.v} value={o.v} className="bg-zinc-950 text-zinc-200">
                ↕ {o.label}{!esOverride && o.v === orden ? " (global)" : ""}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className={`p-1.5 space-y-1.5 min-h-[200px] ${esHistorico ? "max-h-[75vh]" : "max-h-[70vh]"} overflow-y-auto`}>
        {items.length === 0 && (
          <div className="text-[10px] text-zinc-600 italic text-center py-6">Vacío</div>
        )}
        {esHistorico ? (
          // Agrupado por día — colapsable si es antiguo (>14d)
          gruposDia.map((g) => {
            const diasHace = Math.floor((Date.now() - new Date(g.iso).getTime()) / 86400000);
            const collapsedByDefault = diasHace > 14;
            const isCollapsed = colapsados[g.iso] === undefined ? collapsedByDefault : colapsados[g.iso];
            const toggle = () => setColapsados((prev) => ({ ...prev, [g.iso]: !isCollapsed }));
            return (
              <div key={g.iso} data-testid={`historico-dia-${g.iso}`}>
                <button onClick={toggle}
                  className="w-full flex items-center justify-between text-[10px] uppercase tracking-wider text-amber-300/80 font-semibold px-1 py-1 hover:bg-zinc-900/60 rounded">
                  <span className="capitalize">{isCollapsed ? "▶" : "▼"} {g.label}</span>
                  <span className="text-zinc-500 font-mono normal-case">{g.items.length}</span>
                </button>
                {!isCollapsed && (
                  <div className="space-y-1.5 pb-1">
                    {g.items.map((c) => (
                      <CardComp key={c.id} c={c}
                        onOpenDetail={onOpenDetail}
                        onCopiar={onCopiar}
                        compacto={modoCompacto}
                        soloLectura />
                    ))}
                  </div>
                )}
              </div>
            );
          })
        ) : gruposMaterial ? (
          gruposMaterial.map((g) => (
            <div key={g.key} data-testid={`grupo-material-${col.key}-${g.key}`}>
              <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-amber-300/80 font-semibold px-1 py-1 border-b border-zinc-800/60 mb-1">
                <span className="truncate" title={g.label}>🏭 {g.label}</span>
                <span className="text-zinc-500 font-mono normal-case shrink-0">{g.items.length}{g.m2 > 0 ? ` · ${g.m2} m²` : ""}</span>
              </div>
              <div className="space-y-1.5 pb-1">
                {g.items.map((c, idx) => renderCard(c, idx, g.items))}
              </div>
            </div>
          ))
        ) : (
          items.map((c, idx) => renderCard(c, idx, items))
        )}
        {producidoInfo && (producidoInfo.ocultos > 0 || producidoInfo.verTodos) && (
          <button
            onClick={producidoInfo.onToggle}
            data-testid="toggle-ver-todos-producidos"
            className="w-full text-[10px] py-1.5 rounded bg-amber-500/10 hover:bg-amber-500/25 text-amber-300 font-semibold transition-colors">
            {producidoInfo.verTodos
              ? "▲ Ver solo últimos 30 días"
              : `▼ Ver todos (+${producidoInfo.ocultos} antiguos)`}
          </button>
        )}
      </div>
    </div>
  );
}

function DraggableCard({ c, siguiente, onSiguiente, onOpenDetail, onCopiar, onArchivar, onEtiqueta, mover, compacto }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: c.id });
  return (
    <div ref={setNodeRef} {...attributes} {...listeners}
         style={{ opacity: isDragging ? 0.3 : 1 }}
         data-testid={`card-${c.id}`}>
      <CardComp c={c} siguiente={siguiente} onSiguiente={onSiguiente}
                onOpenDetail={onOpenDetail} onCopiar={onCopiar} onArchivar={onArchivar} onEtiqueta={onEtiqueta}
                compacto={compacto} mover={mover} />
    </div>
  );
}

function CardComp({ c, siguiente, onSiguiente, onOpenDetail, onCopiar, onArchivar, onEtiqueta,
                    arrastrando, mover, compacto, soloLectura }) {
  const dias_obj = c.peticion?.dias_a_objetivo;
  const vencido = dias_obj !== null && dias_obj !== undefined && dias_obj < 0;
  const urgente = dias_obj !== null && dias_obj !== undefined && dias_obj >= 0 && dias_obj <= 3;
  const provCls = provinciaColor(c.espacio?.provincia);
  const fechaMontaje = c.fecha_montaje
    ? new Date(c.fecha_montaje).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit" })
    : null;
  const fechaObjetivo = c.peticion?.fecha_objetivo
    ? new Date(c.peticion.fecha_objetivo).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" })
    : null;
  const cantidad = Number(c.cantidad || 1);
  const stopP = (e) => e.stopPropagation();
  const stopDown = (e) => e.stopPropagation();

  return (
    <div className={`bg-zinc-950/80 border rounded-md ${compacto ? "p-1.5" : "p-2"} cursor-grab active:cursor-grabbing
                     ${vencido ? "border-red-500/50" : urgente ? "border-amber-500/50" : "border-zinc-800"}
                     ${arrastrando ? "shadow-2xl shadow-amber-500/20 rotate-2" : "hover:border-zinc-700"} transition-all`}>
      {/* Fila 1: Código DNF + acciones */}
      <div className="flex items-start justify-between gap-1 mb-1">
        <div className="flex items-center gap-1 min-w-0 flex-1">
          <span className="text-[10px] text-amber-400 font-mono truncate">{c.peticion?.codigo}</span>
          {onCopiar && !arrastrando && c.peticion?.codigo && (
            <button
              onPointerDown={stopDown} onClick={(e) => { stopP(e); onCopiar(c.peticion.codigo); }}
              data-testid={`card-copy-${c.id}`}
              title="Copiar código"
              className="text-zinc-600 hover:text-amber-300 rounded p-0.5 transition-colors text-[10px]">
              📋
            </button>
          )}
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {mover && !arrastrando && (
            <>
              <button
                onPointerDown={stopDown}
                onClick={(e) => { stopP(e); mover.up && mover.up(); }}
                disabled={!mover.up}
                data-testid={`card-up-${c.id}`}
                title="Subir"
                className="text-zinc-500 hover:text-amber-300 hover:bg-amber-500/10 rounded p-0.5 transition-colors disabled:opacity-20 disabled:pointer-events-none">
                <ArrowUp className="w-3 h-3" />
              </button>
              <button
                onPointerDown={stopDown}
                onClick={(e) => { stopP(e); mover.down && mover.down(); }}
                disabled={!mover.down}
                data-testid={`card-down-${c.id}`}
                title="Bajar"
                className="text-zinc-500 hover:text-amber-300 hover:bg-amber-500/10 rounded p-0.5 transition-colors disabled:opacity-20 disabled:pointer-events-none">
                <ArrowDown className="w-3 h-3" />
              </button>
            </>
          )}
          {(c.aaff_total || 0) > 0 && (
            <span
              data-testid={`card-aaff-${c.id}`}
              title={c.aaff_total === 1 ? "AAFF disponible (1)" : `${c.aaff_total} AAFF disponibles`}
              className="inline-flex items-center gap-0.5 text-[9px] px-1 rounded bg-sky-500/15 text-sky-300 border border-sky-500/30 font-mono">
              <Paperclip className="w-2.5 h-2.5" />{c.aaff_total}
            </span>
          )}
          {vencido && <span className="text-[9px] bg-red-500/30 text-red-200 px-1 rounded font-bold animate-pulse" data-testid={`card-vencido-${c.id}`}>⚠ VENC{fechaObjetivo ? ` ${fechaObjetivo}` : ""}</span>}
          {!vencido && urgente && <span className="text-[9px] bg-amber-500/30 text-amber-200 px-1 rounded font-bold" data-testid={`card-urgente-${c.id}`}>⏰{dias_obj}d{fechaObjetivo ? ` · ${fechaObjetivo}` : ""}</span>}
          {!vencido && !urgente && fechaObjetivo && (
            <span className="text-[9px] bg-zinc-800 text-zinc-400 px-1 rounded font-mono" title={`Fecha objetivo · ${dias_obj}d`} data-testid={`card-objetivo-${c.id}`}>
              🎯 {fechaObjetivo}{dias_obj !== null && dias_obj !== undefined ? ` · ${dias_obj}d` : ""}
            </span>
          )}
          {onEtiqueta && !arrastrando && !soloLectura && (
            <button
              onPointerDown={stopDown} onClick={(e) => { stopP(e); onEtiqueta(c.id); }}
              data-testid={`card-etiqueta-${c.id}`}
              title={c.etiqueta_impresa_at ? "Reimprimir etiqueta" : "Imprimir etiqueta"}
              className={`rounded p-0.5 transition-colors ${c.etiqueta_impresa_at ? "text-zinc-600 hover:text-amber-300" : "text-amber-300 hover:bg-amber-500/10"}`}>
              <Printer className="w-3 h-3" />
            </button>
          )}
          {onArchivar && !arrastrando && (
            <button
              onPointerDown={stopDown} onClick={(e) => { stopP(e); onArchivar(c.id); }}
              data-testid={`card-archivar-${c.id}`}
              title="Enviar a histórico (>7 días)"
              className="text-zinc-500 hover:text-emerald-300 hover:bg-emerald-500/10 rounded p-0.5 transition-colors">
              📚
            </button>
          )}
          {onOpenDetail && !arrastrando && (
            <button
              onPointerDown={stopDown} onClick={(e) => { stopP(e); onOpenDetail(c.id); }}
              data-testid={`card-detail-${c.id}`}
              title="Ver detalle"
              className="text-zinc-500 hover:text-amber-300 hover:bg-amber-500/10 rounded p-0.5 transition-colors">
              <Eye className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* Fila 2: contenido principal — con thumb si aplica */}
      <div className="flex gap-2">
        {(c.imagen_url || c.peticion?.imagen_url) && !compacto && (
          <img src={c.imagen_url || c.peticion?.imagen_url}
               alt="" loading="lazy"
               className="w-10 h-10 object-cover rounded shrink-0 border border-zinc-800" />
        )}
        <div className="min-w-0 flex-1 space-y-0.5">
          {/* Fecha de montaje */}
          {fechaMontaje && (
            <div className="text-[10px] text-zinc-300 font-mono flex items-center gap-1">
              <Calendar className="w-2.5 h-2.5 text-amber-400" /> {fechaMontaje}
            </div>
          )}
          {/* Cliente */}
          <div className={`${compacto ? "text-[10px]" : "text-[11px]"} font-semibold text-zinc-100 truncate flex items-center gap-1`}>
            <span className="truncate">{c.cliente?.nombre || "—"}</span>
            {cantidad > 1 && (
              <span className="shrink-0 text-[9px] px-1 rounded bg-amber-500/20 text-amber-200 border border-amber-500/40 font-mono" title={`${cantidad} unidades`} data-testid={`card-cantidad-${c.id}`}>×{cantidad}</span>
            )}
          </div>
          {/* Marca */}
          {c.peticion?.marca && (
            <div className="inline-flex items-center gap-0.5 text-[9px] px-1 rounded bg-violet-500/15 text-violet-300">
              <Tag className="w-2 h-2" /> {c.peticion.marca}
            </div>
          )}
          {/* Espacio */}
          {c.espacio?.nombre && (
            <div className="text-[10px] text-zinc-400 truncate" title={c.espacio.nombre}>
              📍 {c.espacio.nombre}
            </div>
          )}
          {!compacto && (
            <>
              {/* Medidas + material */}
              {(c.medidas || c.material?.nombre) && (
                <div className="text-[9px] text-zinc-500 truncate">
                  {c.medidas && <span className="font-mono">{c.medidas}</span>}
                  {c.medidas && c.material?.nombre && <span> · </span>}
                  {c.material?.nombre && <span>{c.material.nombre}</span>}
                </div>
              )}
            </>
          )}
          {/* Provincia coloreada */}
          {c.espacio?.provincia && (
            <div className={`inline-block text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${provCls} mt-0.5`}>
              {c.espacio.provincia}
            </div>
          )}
        </div>
      </div>

      {/* Nombre del componente (segunda línea si hay espacio) */}
      {c.nombre && !compacto && (
        <div className="text-[10px] text-zinc-500 mt-1 line-clamp-1 italic">{c.nombre}</div>
      )}
      {/* Cambio de gráfica aprobado desde correo (qué hay que fabricar/instalar) */}
      {c.cambio_grafica && <div className="mt-1 flex flex-wrap gap-1"><GraficaChip cg={c.cambio_grafica} testId={`card-grafica-${c.id}`} /></div>}

      {/* Sub-piezas marco de vinilo */}
      {c.tipo_plantilla === "marco_vinilo" && (c.subpiezas_count || 0) > 0 && (
        <div className="mt-1 inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 font-mono">
          📦 {c.subpiezas_count} sub-piezas
        </div>
      )}

      {/* Días en columna */}
      {c.dias_en_columna !== null && c.dias_en_columna > 2 && (
        <div className="mt-1 text-[9px] text-zinc-500 flex items-center gap-0.5">
          <Clock className="w-2.5 h-2.5" /> {c.dias_en_columna}d en columna
        </div>
      )}

      {/* Botón siguiente (no en histórico ni compacto extremo) */}
      {siguiente && !arrastrando && onSiguiente && !soloLectura && (
        <button
          onPointerDown={stopDown}
          onClick={(e) => { stopP(e); onSiguiente(c.id, siguiente); }}
          data-testid={`btn-siguiente-${c.id}`}
          className="mt-1.5 w-full text-[10px] py-1 rounded bg-amber-500/15 hover:bg-amber-500/30
                     text-amber-200 hover:text-amber-100 transition-colors flex items-center justify-center gap-0.5 font-semibold"
          title={`Pasar a "${siguiente}"`}>
          → siguiente <ChevronRight className="w-2.5 h-2.5" />
        </button>
      )}
    </div>
  );
}
